/**
 * Messages the portal sends to the Astrato iframe after a campaign is saved (RUN_CAMPAIGN).
 *
 * Astrato shows a notification for every CAMPAIGN_RESPONSE it receives, so the async flow needs NO change on its
 * side: one notification when the run is accepted (running | queued) and one when it ends. CAMPAIGN_PROGRESS
 * (per-batch progress) is optional for Astrato: if it ignores the type, nothing breaks.
 *
 * Synchronous vendors keep the exact messages they always had.
 */

const STARTED_MESSAGE = "Execução iniciada. Você será notificado ao final.";
const BUSY_MESSAGE =
  "Esta campanha tem uma execução em andamento. Ela será cancelada e sua alteração atual terá prioridade: será executada logo em seguida.";
const FINAL_STATUSES = new Set(["success", "partial_success", "failed"]);

const counters = ({ total = 0, processed = 0, succeeded = 0, failed = 0 } = {}) =>
  `Total: ${total}|$|Processed: ${processed}|$|Succeeded: ${succeeded}|$|Failed: ${failed}`;

const campaignResponse = (name, status, progress, message) => ({
  type: "CAMPAIGN_RESPONSE",
  payload: `Campaign ${name} status: ${status}|$|${counters(progress)}${message ? `|$|Message: ${message}` : ""}`,
});

const campaignFailed = (name, error) => ({
  type: "CAMPAIGN_RESPONSE",
  payload: `Campaign ${name} status: failed|$|Error: ${error}|$|${counters()}`,
});

/** Run accepted by the backend: tell the user now, then follow the run's log until it ends. */
function watchAsyncRun(data, { db, send, log }) {
  const { logId, state } = data;
  const name = data.campaign_name;

  send(
    state === "queued"
      ? campaignResponse(name, "queued", undefined, BUSY_MESSAGE)
      : campaignResponse(name, "running", undefined, STARTED_MESSAGE),
  );

  let finished = false;
  let lastProgressKey = null;
  const unsubscribe = db
    .collection("campaigns_logs")
    .doc(logId)
    .onSnapshot(
      (snapshot) => {
        if (finished || !snapshot.exists) return;
        const run = snapshot.data();
        const runName = run.campaign_name || name;

        if (FINAL_STATUSES.has(run.status)) {
          finished = true;
          send(
            run.status === "failed" && run.error
              ? campaignFailed(runName, run.error)
              : campaignResponse(runName, run.status, run.progress),
          );
          unsubscribe();
        } else if (run.status === "cancelled") {
          // Replaced by a newer Save: the newer run notifies on its own.
          finished = true;
          unsubscribe();
        } else if (run.status === "running" && run.total_batches != null) {
          const key = `${run.done_batches}|${run.progress?.processed}`;
          if (key === lastProgressKey) return;
          lastProgressKey = key;
          send({
            type: "CAMPAIGN_PROGRESS",
            payload: `Campaign ${runName} status: running|$|${counters(run.progress)}|$|Batch: ${run.done_batches}/${run.total_batches}`,
          });
        }
        // waiting | queued | cancelling: the user was already told at Save time.
      },
      (error) => log.error("# ENO # - campaigns_logs listener error", error),
    );

  return unsubscribe;
}

/** Synchronous vendors: same messages as before the async flow existed. */
function sendSyncResult(data, send) {
  const { campaign_name, status, result, message: vendorMessage } = data;

  if (status === "failed") {
    send(campaignFailed(campaign_name, result?.error));
    return;
  }
  if (result && typeof result === "object") {
    const vendorKey = Object.keys(result)[0];
    const progress = result[vendorKey]?.progress;
    if (campaign_name && progress) {
      send(campaignResponse(campaign_name, status, progress, vendorMessage));
    }
  }
}

/**
 * Handles the response of functions.campaigns("push"). Returns an unsubscribe function when it started listening
 * to an async run (call it on unmount), otherwise null.
 */
export function handleCampaignPushResponse(response, { db, send, log }) {
  const data = response?.data;
  if (!data) return null;

  if (data.logId && (data.state === "started" || data.state === "queued")) {
    return watchAsyncRun(data, { db, send, log });
  }

  sendSyncResult(data, send);
  return null;
}
